import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { apiFetch } from "@/lib/api-fetch";
import { IS_LOCAL_DEMO } from "@/components/demo/DemoPackageSwitch";
import {
  Users,
  UserPlus,
  Mail,
  Shield,
  Clock,
  MoreVertical,
  X,
  Copy,
  Check,
  Trash2,
  Pencil,
  Link2,
  AlertTriangle,
  Loader2,
  Phone,
  Bell,
} from "lucide-react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type TeamMember = {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  role: string;
  features: string[];
  notifications: string[];
  last_login_at: string | null;
};

type Invitation = {
  id: string;
  email: string;
  role: string;
  features: string[];
  sent_at: string;
  expires_at: string;
  invite_link: string;
};

// ---------------------------------------------------------------------------
// Badge config
// ---------------------------------------------------------------------------

const ROLE_BADGE: Record<string, { label: string; bg: string; text: string }> = {
  admin: { label: "Admin", bg: "bg-purple-100 dark:bg-purple-900/30", text: "text-purple-700 dark:text-purple-400" },
  manager: { label: "Manager", bg: "bg-blue-100 dark:bg-blue-900/30", text: "text-blue-700 dark:text-blue-400" },
  operator: { label: "Operator", bg: "bg-green-100 dark:bg-green-900/30", text: "text-green-700 dark:text-green-400" },
};

const FEATURE_BADGE: Record<string, { label: string; bg: string; text: string }> = {
  composting: { label: "Composting", bg: "bg-green-100 dark:bg-green-900/30", text: "text-green-700 dark:text-green-400" },
  operations: { label: "Operations", bg: "bg-blue-100 dark:bg-blue-900/30", text: "text-blue-700 dark:text-blue-400" },
  admin: { label: "Admin", bg: "bg-purple-100 dark:bg-purple-900/30", text: "text-purple-700 dark:text-purple-400" },
  platform_admin: { label: "Platform", bg: "bg-amber-100 dark:bg-amber-900/30", text: "text-amber-700 dark:text-amber-400" },
};

const NOTIFICATION_OPTIONS = [
  { value: "new_work_order", label: "New Work Order" },
  { value: "work_order_updated", label: "Work Order Updated" },
  { value: "new_bol", label: "New BOL" },
  { value: "new_cod", label: "New COD" },
] as const;

const NOTIFICATION_BADGE: Record<string, { label: string; bg: string; text: string }> = {
  new_work_order: { label: "Work Orders", bg: "bg-amber-100 dark:bg-amber-900/30", text: "text-amber-700 dark:text-amber-400" },
  work_order_updated: { label: "WO Updates", bg: "bg-orange-100 dark:bg-orange-900/30", text: "text-orange-700 dark:text-orange-400" },
  new_bol: { label: "BOLs", bg: "bg-cyan-100 dark:bg-cyan-900/30", text: "text-cyan-700 dark:text-cyan-400" },
  new_cod: { label: "CODs", bg: "bg-teal-100 dark:bg-teal-900/30", text: "text-teal-700 dark:text-teal-400" },
};

function Badge({ config }: { config: { label: string; bg: string; text: string } }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${config.bg} ${config.text}`}>
      {config.label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(dateStr: string | null) {
  if (!dateStr) return "Never";
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function isExpired(dateStr: string) {
  return new Date(dateStr) < new Date();
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export default function TeamPage() {
  const { token, user } = useAuth();
  const queryClient = useQueryClient();

  // UI state
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [editingMember, setEditingMember] = useState<TeamMember | null>(null);
  const [deletingMemberId, setDeletingMemberId] = useState<string | null>(null);
  const [cancellingInviteId, setCancellingInviteId] = useState<string | null>(null);
  const [actionsOpenId, setActionsOpenId] = useState<string | null>(null);
  const [copiedLinkId, setCopiedLinkId] = useState<string | null>(null);

  // -------------------------------------------------------------------------
  // Queries
  // -------------------------------------------------------------------------

  const {
    data: members = [],
    isLoading: membersLoading,
    error: membersError,
  } = useQuery<TeamMember[]>({
    queryKey: ["team-members"],
    queryFn: async () => {
      const res = await apiFetch("/api/team", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Failed to load team (${res.status})`);
      const data = await res.json();
      return data.members ?? data;
    },
    enabled: !!token,
  });

  const {
    data: invitations = [],
    isLoading: invitationsLoading,
  } = useQuery<Invitation[]>({
    queryKey: ["team-invitations"],
    queryFn: async () => {
      const res = await apiFetch("/api/team/invitations", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Failed to load invitations (${res.status})`);
      const data = await res.json();
      return data.invitations ?? data;
    },
    enabled: !!token,
  });

  // -------------------------------------------------------------------------
  // Mutations
  // -------------------------------------------------------------------------

  const removeMemberMutation = useMutation({
    mutationFn: async (memberId: string) => {
      const res = await apiFetch(`/api/team/members/${memberId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to remove member");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["team-members"] });
      setDeletingMemberId(null);
    },
  });

  const updateMemberMutation = useMutation({
    mutationFn: async ({ id, role, features, phone, notifications }: { id: string; role: string; features: string[]; phone: string; notifications: string[] }) => {
      const res = await apiFetch(`/api/team/members/${id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ role, features, phone, notifications }),
      });
      if (!res.ok) throw new Error("Failed to update member");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["team-members"] });
      setEditingMember(null);
    },
  });

  const cancelInviteMutation = useMutation({
    mutationFn: async (inviteId: string) => {
      const res = await apiFetch(`/api/team/invitations/${inviteId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to cancel invitation");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["team-invitations"] });
      setCancellingInviteId(null);
    },
  });

  // -------------------------------------------------------------------------
  // Copy to clipboard helper
  // -------------------------------------------------------------------------

  async function copyInviteLink(inviteId: string, link: string) {
    try {
      await navigator.clipboard.writeText(link);
      setCopiedLinkId(inviteId);
      setTimeout(() => setCopiedLinkId(null), 2000);
    } catch {
      // Fallback: no-op
    }
  }

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <div className="space-y-8 max-w-5xl">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Team Management</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {IS_LOCAL_DEMO ? "Preview the sample team and its roles" : "Manage team members, roles, and invitations"}
          </p>
        </div>
        {!IS_LOCAL_DEMO && <button
          onClick={() => setShowInviteModal(true)}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-forest-600 hover:bg-forest-700 text-white text-sm font-semibold rounded-xl transition-colors shadow-sm shadow-forest-600/20"
        >
          <UserPlus size={16} strokeWidth={2.5} />
          Invite Team Member
        </button>}
      </div>

      {/* ----------------------------------------------------------------- */}
      {/* Team Members */}
      {/* ----------------------------------------------------------------- */}
      <section>
        <div className="flex items-center gap-2 mb-4">
          <Users size={18} className="text-forest-500" />
          <h2 className="text-lg font-semibold text-foreground">
            Team Members
            {!membersLoading && (
              <span className="ml-2 text-sm font-normal text-muted-foreground">
                ({members.length})
              </span>
            )}
          </h2>
        </div>

        {membersLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-card border border-border rounded-xl p-5 animate-pulse">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-muted" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-muted rounded w-32" />
                    <div className="h-3 bg-muted rounded w-48" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : membersError ? (
          <div className="bg-card border border-border rounded-xl p-12 text-center">
            <AlertTriangle size={24} className="mx-auto text-red-500 mb-2" />
            <p className="text-sm text-red-600 dark:text-red-400">
              {membersError instanceof Error ? membersError.message : "Failed to load team members"}
            </p>
          </div>
        ) : members.length === 0 ? (
          <div className="bg-card border border-border rounded-xl p-12 text-center">
            <div className="w-12 h-12 mx-auto rounded-xl bg-secondary flex items-center justify-center mb-3">
              <Users size={24} className="text-muted-foreground" />
            </div>
            <p className="text-muted-foreground">
              No team members yet. Invite your first team member to get started.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {members.map((member) => {
              const roleCfg = ROLE_BADGE[member.role] || ROLE_BADGE.operator;
              const isCurrentUser = user?.id === member.id;

              return (
                <div
                  key={member.id}
                  className="bg-card border border-border rounded-xl p-5 hover:border-forest-200 dark:hover:border-forest-800 transition-colors"
                >
                  <div className="flex items-start sm:items-center justify-between gap-4">
                    {/* Left: avatar + info */}
                    <div className="flex items-center gap-4 min-w-0 flex-1">
                      <div className="w-10 h-10 rounded-full bg-forest-100 dark:bg-forest-900/30 flex items-center justify-center shrink-0">
                        <span className="text-sm font-bold text-forest-700 dark:text-forest-400">
                          {member.name
                            .split(" ")
                            .map((n) => n[0])
                            .join("")
                            .toUpperCase()
                            .slice(0, 2)}
                        </span>
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm font-semibold text-foreground truncate">
                            {member.name}
                          </p>
                          {isCurrentUser && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-forest-100 dark:bg-forest-900/30 text-forest-700 dark:text-forest-400">
                              (You)
                            </span>
                          )}
                          <Badge config={roleCfg} />
                        </div>
                        <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                          <div className="flex items-center gap-1.5">
                            <Mail size={12} className="text-muted-foreground shrink-0" />
                            <span className="text-xs text-muted-foreground truncate">
                              {member.email}
                            </span>
                          </div>
                          {member.phone && (
                            <div className="flex items-center gap-1.5">
                              <Phone size={12} className="text-muted-foreground shrink-0" />
                              <span className="text-xs text-muted-foreground">
                                {member.phone}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-2 mt-2 flex-wrap">
                          {member.features?.map((f) => {
                            const cfg = FEATURE_BADGE[f];
                            return cfg ? <Badge key={f} config={cfg} /> : null;
                          })}
                        </div>
                        {member.notifications?.length > 0 && (
                          <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                            <Bell size={11} className="text-amber-500 shrink-0" />
                            {member.notifications.map((n) => {
                              const cfg = NOTIFICATION_BADGE[n];
                              return cfg ? <Badge key={n} config={cfg} /> : null;
                            })}
                          </div>
                        )}
                        <div className="flex items-center gap-1 mt-2">
                          <Clock size={11} className="text-muted-foreground" />
                          <span className="text-[11px] text-muted-foreground">
                            Last login: {formatDate(member.last_login_at)}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Right: actions */}
                    {!IS_LOCAL_DEMO && !isCurrentUser && (
                      <div className="relative shrink-0">
                        <button
                          onClick={() => setActionsOpenId(actionsOpenId === member.id ? null : member.id)}
                          className="p-2 text-muted-foreground hover:text-foreground hover:bg-secondary rounded-lg transition-colors"
                        >
                          <MoreVertical size={16} />
                        </button>

                        {actionsOpenId === member.id && (
                          <>
                            <div
                              className="fixed inset-0 z-10"
                              onClick={() => setActionsOpenId(null)}
                            />
                            <div className="absolute right-0 top-full mt-1 z-20 bg-card border border-border rounded-lg shadow-lg py-1 min-w-[160px]">
                              <button
                                onClick={() => {
                                  setActionsOpenId(null);
                                  setEditingMember(member);
                                }}
                                className="flex items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-secondary transition-colors w-full text-left"
                              >
                                <Pencil size={14} />
                                Edit Role
                              </button>
                              <div className="border-t border-border my-1" />
                              <button
                                onClick={() => {
                                  setActionsOpenId(null);
                                  setDeletingMemberId(member.id);
                                }}
                                className="flex items-center gap-2 px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors w-full text-left"
                              >
                                <Trash2 size={14} />
                                Remove
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* ----------------------------------------------------------------- */}
      {/* Pending Invitations */}
      {/* ----------------------------------------------------------------- */}
      {!IS_LOCAL_DEMO && <section>
        <div className="flex items-center gap-2 mb-4">
          <Mail size={18} className="text-forest-500" />
          <h2 className="text-lg font-semibold text-foreground">
            Pending Invitations
            {!invitationsLoading && invitations.length > 0 && (
              <span className="ml-2 text-sm font-normal text-muted-foreground">
                ({invitations.length})
              </span>
            )}
          </h2>
        </div>

        {invitationsLoading ? (
          <div className="space-y-3">
            {[1, 2].map((i) => (
              <div key={i} className="bg-card border border-border rounded-xl p-5 animate-pulse">
                <div className="h-4 bg-muted rounded w-48 mb-2" />
                <div className="h-3 bg-muted rounded w-64" />
              </div>
            ))}
          </div>
        ) : invitations.length === 0 ? (
          <div className="bg-card border border-border rounded-xl p-8 text-center">
            <p className="text-sm text-muted-foreground">No pending invitations.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {invitations.map((invite) => {
              const roleCfg = ROLE_BADGE[invite.role] || ROLE_BADGE.operator;
              const expired = isExpired(invite.expires_at);

              return (
                <div
                  key={invite.id}
                  className={`bg-card border rounded-xl p-5 transition-colors ${
                    expired
                      ? "border-red-200 dark:border-red-900/40"
                      : "border-border hover:border-forest-200 dark:hover:border-forest-800"
                  }`}
                >
                  <div className="flex items-start sm:items-center justify-between gap-4 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-semibold text-foreground">{invite.email}</p>
                        <Badge config={roleCfg} />
                        {expired && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400">
                            Expired
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        {invite.features?.map((f) => {
                          const cfg = FEATURE_BADGE[f];
                          return cfg ? <Badge key={f} config={cfg} /> : null;
                        })}
                      </div>
                      <div className="flex items-center gap-4 mt-2 text-[11px] text-muted-foreground">
                        <span>Sent: {formatDate(invite.sent_at)}</span>
                        <span>
                          {expired ? "Expired" : `Expires: ${formatDate(invite.expires_at)}`}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        onClick={() => copyInviteLink(invite.id, invite.invite_link)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-secondary hover:bg-muted text-xs font-medium text-foreground rounded-lg transition-colors"
                        title="Copy invite link"
                      >
                        {copiedLinkId === invite.id ? (
                          <>
                            <Check size={13} className="text-green-500" />
                            Copied
                          </>
                        ) : (
                          <>
                            <Link2 size={13} />
                            Copy Link
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => setCancellingInviteId(invite.id)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-lg transition-colors"
                      >
                        <X size={13} />
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>}

      {/* ----------------------------------------------------------------- */}
      {/* Invite Modal */}
      {/* ----------------------------------------------------------------- */}
      {showInviteModal && (
        <InviteModal
          token={token}
          onClose={() => setShowInviteModal(false)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ["team-invitations"] });
          }}
        />
      )}

      {/* ----------------------------------------------------------------- */}
      {/* Edit Member Modal */}
      {/* ----------------------------------------------------------------- */}
      {editingMember && (
        <EditMemberModal
          member={editingMember}
          isPending={updateMemberMutation.isPending}
          onClose={() => setEditingMember(null)}
          onSave={(role, features, phone, notifications) =>
            updateMemberMutation.mutate({ id: editingMember.id, role, features, phone, notifications })
          }
        />
      )}

      {/* ----------------------------------------------------------------- */}
      {/* Delete Member Confirmation */}
      {/* ----------------------------------------------------------------- */}
      {deletingMemberId && (
        <ConfirmModal
          title="Remove Team Member"
          message="Are you sure you want to remove this team member? They will lose access to the application immediately."
          confirmLabel="Remove"
          isPending={removeMemberMutation.isPending}
          onCancel={() => setDeletingMemberId(null)}
          onConfirm={() => removeMemberMutation.mutate(deletingMemberId)}
        />
      )}

      {/* ----------------------------------------------------------------- */}
      {/* Cancel Invitation Confirmation */}
      {/* ----------------------------------------------------------------- */}
      {cancellingInviteId && (
        <ConfirmModal
          title="Cancel Invitation"
          message="Are you sure you want to cancel this invitation? The invite link will no longer work."
          confirmLabel="Cancel Invitation"
          isPending={cancelInviteMutation.isPending}
          onCancel={() => setCancellingInviteId(null)}
          onConfirm={() => cancelInviteMutation.mutate(cancellingInviteId)}
        />
      )}
    </div>
  );
}

// ===========================================================================
// Invite Modal
// ===========================================================================

function InviteModal({
  token,
  onClose,
  onSuccess,
}: {
  token: string | null;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [role, setRole] = useState("operator");
  const [features, setFeatures] = useState<string[]>(["composting"]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createdLink, setCreatedLink] = useState<string | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);

  function toggleFeature(f: string) {
    setFeatures((prev) =>
      prev.includes(f) ? prev.filter((x) => x !== f) : [...prev, f]
    );
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim()) return;
    setSending(true);
    setError(null);

    try {
      const res = await apiFetch("/api/team/invite", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ email: email.trim(), role, features, phone: phone.trim() || undefined }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || `Failed to send invitation (${res.status})`);
        return;
      }
      setCreatedLink(data.invite_link || data.inviteLink || null);
      onSuccess();
    } catch {
      setError("Failed to send invitation. Please try again.");
    } finally {
      setSending(false);
    }
  }

  async function copyCreatedLink() {
    if (!createdLink) return;
    try {
      await navigator.clipboard.writeText(createdLink);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      // Fallback: no-op
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-2xl w-full max-w-md shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 pb-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-forest-100 dark:bg-forest-900/30 flex items-center justify-center">
              <UserPlus size={20} className="text-forest-600 dark:text-forest-400" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-foreground">Invite Team Member</h3>
              <p className="text-xs text-muted-foreground">Send an invitation to join your team</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-muted-foreground hover:text-foreground hover:bg-secondary rounded-lg transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Email */}
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5">
              Email Address <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="team@example.com"
              required
              disabled={!!createdLink}
              className="w-full px-3 py-2.5 bg-secondary border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-forest-600 disabled:opacity-60"
            />
          </div>

          {/* Phone */}
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5">
              Phone Number <span className="text-muted-foreground/50">(for SMS notifications)</span>
            </label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="(602) 555-0142"
              disabled={!!createdLink}
              className="w-full px-3 py-2.5 bg-secondary border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-forest-600 disabled:opacity-60"
            />
          </div>

          {/* Role */}
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5">Role</label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              disabled={!!createdLink}
              className="w-full px-3 py-2.5 bg-secondary border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-forest-600 disabled:opacity-60"
            >
              <option value="admin">Admin</option>
              <option value="manager">Manager</option>
              <option value="operator">Operator</option>
            </select>
          </div>

          {/* Features */}
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-2">
              Feature Access
            </label>
            <div className="flex flex-col gap-2.5">
              {(["composting", "operations", "admin"] as const).map((f) => {
                const cfg = FEATURE_BADGE[f];
                return (
                  <label key={f} className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={features.includes(f)}
                      onChange={() => toggleFeature(f)}
                      disabled={!!createdLink}
                      className="rounded border-border text-forest-600 focus:ring-forest-600"
                    />
                    <Badge config={cfg} />
                  </label>
                );
              })}
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg">
              <AlertTriangle size={14} className="text-red-500 shrink-0" />
              <p className="text-xs text-red-700 dark:text-red-400">{error}</p>
            </div>
          )}

          {/* Created link display */}
          {createdLink && (
            <div className="p-3 bg-forest-50 dark:bg-forest-950/30 border border-forest-200 dark:border-forest-800 rounded-lg">
              <p className="text-xs font-medium text-forest-700 dark:text-forest-400 mb-2">
                Invitation sent! Share this link:
              </p>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={createdLink}
                  readOnly
                  className="flex-1 px-2 py-1.5 bg-white dark:bg-card border border-border rounded text-xs text-foreground font-mono truncate"
                />
                <button
                  type="button"
                  onClick={copyCreatedLink}
                  className="shrink-0 inline-flex items-center gap-1 px-3 py-1.5 bg-forest-600 hover:bg-forest-700 text-white text-xs font-medium rounded transition-colors"
                >
                  {linkCopied ? (
                    <>
                      <Check size={12} />
                      Copied
                    </>
                  ) : (
                    <>
                      <Copy size={12} />
                      Copy
                    </>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-secondary"
            >
              {createdLink ? "Done" : "Cancel"}
            </button>
            {!createdLink && (
              <button
                type="submit"
                disabled={sending || !email.trim()}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-forest-600 hover:bg-forest-700 disabled:opacity-50 text-white text-sm font-semibold rounded-xl transition-colors"
              >
                {sending ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Mail size={14} />
                    Send Invitation
                  </>
                )}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

// ===========================================================================
// Edit Member Modal
// ===========================================================================

function EditMemberModal({
  member,
  isPending,
  onClose,
  onSave,
}: {
  member: TeamMember;
  isPending: boolean;
  onClose: () => void;
  onSave: (role: string, features: string[], phone: string, notifications: string[]) => void;
}) {
  const [role, setRole] = useState(member.role);
  const [features, setFeatures] = useState<string[]>(member.features || []);
  const [phone, setPhone] = useState(member.phone || "");
  const [notifications, setNotifications] = useState<string[]>(member.notifications || []);

  function toggleFeature(f: string) {
    setFeatures((prev) =>
      prev.includes(f) ? prev.filter((x) => x !== f) : [...prev, f]
    );
  }

  function toggleNotification(n: string) {
    setNotifications((prev) =>
      prev.includes(n) ? prev.filter((x) => x !== n) : [...prev, n]
    );
  }

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-2xl w-full max-w-md shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 pb-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
              <Shield size={20} className="text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-foreground">Edit Member</h3>
              <p className="text-xs text-muted-foreground">{member.name} ({member.email})</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-muted-foreground hover:text-foreground hover:bg-secondary rounded-lg transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {/* Role */}
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5">Role</label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="w-full px-3 py-2.5 bg-secondary border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-forest-600"
            >
              <option value="admin">Admin</option>
              <option value="manager">Manager</option>
              <option value="operator">Operator</option>
            </select>
          </div>

          {/* Phone */}
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5">
              Phone Number
            </label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="(555) 123-4567"
              className="w-full px-3 py-2.5 bg-secondary border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-forest-600"
            />
          </div>

          {/* Features */}
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-2">
              Feature Access
            </label>
            <div className="flex flex-col gap-2.5">
              {(["composting", "operations", "admin"] as const).map((f) => {
                const cfg = FEATURE_BADGE[f];
                return (
                  <label key={f} className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={features.includes(f)}
                      onChange={() => toggleFeature(f)}
                      className="rounded border-border text-forest-600 focus:ring-forest-600"
                    />
                    <Badge config={cfg} />
                  </label>
                );
              })}
            </div>
          </div>

          {/* Notifications */}
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-2">
              Email Notifications
            </label>
            <div className="flex flex-col gap-2.5">
              {NOTIFICATION_OPTIONS.map((opt) => {
                const cfg = NOTIFICATION_BADGE[opt.value];
                return (
                  <label key={opt.value} className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={notifications.includes(opt.value)}
                      onChange={() => toggleNotification(opt.value)}
                      className="rounded border-border text-amber-600 focus:ring-amber-600"
                    />
                    {cfg ? <Badge config={cfg} /> : <span className="text-sm">{opt.label}</span>}
                  </label>
                );
              })}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2">
            <button
              onClick={onClose}
              className="px-4 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-secondary"
            >
              Cancel
            </button>
            <button
              onClick={() => onSave(role, features, phone, notifications)}
              disabled={isPending}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-forest-600 hover:bg-forest-700 disabled:opacity-50 text-white text-sm font-semibold rounded-xl transition-colors"
            >
              {isPending ? (
                <>
                  <Loader2 size={14} className="animate-spin" />
                  Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ===========================================================================
// Confirm Modal (reusable)
// ===========================================================================

function ConfirmModal({
  title,
  message,
  confirmLabel,
  isPending,
  onCancel,
  onConfirm,
}: {
  title: string;
  message: string;
  confirmLabel: string;
  isPending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-2xl w-full max-w-md shadow-2xl">
        <div className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
              <AlertTriangle size={20} className="text-red-600 dark:text-red-400" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-foreground">{title}</h3>
              <p className="text-sm text-muted-foreground">{message}</p>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-3 px-6 pb-6">
          <button
            onClick={onCancel}
            className="px-4 py-2.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-secondary"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isPending}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-sm font-semibold rounded-xl transition-colors"
          >
            {isPending ? (
              <>
                <Loader2 size={14} className="animate-spin" />
                Processing...
              </>
            ) : (
              confirmLabel
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
